import { AnyAction } from 'redux';
import {
  AngebotsId,
  CheckoutProdukt,
  GlobalActions,
  ProduktDeleted,
  VorversicherungAblaufdatumChanged,
  VorversicherungChanged,
} from '@ps20-frontend/shop-integration-redux';
import { App, getSpartenTypeByApp } from '@ps20-frontend/frontend-models';
import { GegenstandCategory, SofortschutzAvailableItem } from '@ps20/bff-models';
import { GegeCheckoutProdukt } from '../model';
import { CheckoutActions } from '../checkout.actions';
import { Vorversicherung, VorversicherungsItem } from '../model/vorversicherung';
import { getVersicherteGefahren } from '../data/vorversicherung';
import { VorversicherungDeleteItem } from '../action-types/vorversicherungDeleteItem';
import { VorversicherungsListeGeaendert } from '../action-types/vorversicherungsListeGeaendert';
import { VorversicherungsListeVersicherteGefahrenAendern } from '../action-types/vorversicherungsListeVersicherteGefahrenAendern';
import { IAppState } from '..';

const MAX_VERSICHERUNGSLISTE_LENGTH = 3;

function replaceMatching(
  list: Array<Vorversicherung>,
  appId: App,
  angebotsId: string,
  updater: (vv: Vorversicherung) => Vorversicherung,
): Array<Vorversicherung> {
  let changed = false;
  const next = list.map(vv => {
    if (vv.appId === appId && vv.angebotsId === angebotsId) {
      const updated = updater(vv);
      if (updated !== vv) {
        changed = true;
      }
      return updated;
    }
    return vv;
  });
  return changed ? next : list;
}

function replaceListItem(
  list: Array<VorversicherungsItem>,
  index: number,
  updater: (item: VorversicherungsItem) => VorversicherungsItem,
): Array<VorversicherungsItem> {
  if (index < 0 || index >= list.length) {
    return list;
  }
  const next = list.slice();
  next[index] = updater(list[index]);
  return next;
}

export const vorversicherungReducer = function (
  sectionState: Array<Vorversicherung> = null,
  action: AnyAction,
  state: IAppState,
): Array<Vorversicherung> {
  switch (action.type) {
    case GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED: {
      const actionPayload: VorversicherungChanged = action.payload;
      const { appId, angebotsId } = actionPayload;

      const existingVorversicherung = sectionState.some(
        elem => elem.appId === appId && elem.angebotsId === angebotsId,
      );
      if (!existingVorversicherung) {
        return sectionState;
      }

      return replaceMatching(sectionState, appId, angebotsId, sparte => {
        if (actionPayload.vorhanden) {
          return {
            ...sparte,
            vorhanden: actionPayload.vorhanden,
            vorversicherungCanBeAdded: true,
            vorversicherungsListe: [
              {
                versicherungsgesellschaft: null,
                vsnr: null,
                versicherteGefahren: sparte.gefahrenFromErsatz
                  ? sparte.vorversicherungsListe[0].versicherteGefahren
                  : [...getVersicherteGefahren(appId)],
                ablaufdatum: null,
              },
            ],
          };
        }
        return {
          ...sparte,
          vorhanden: actionPayload.vorhanden,
          vorversicherungCanBeAdded: false,
          vorversicherungsListe: sparte.gefahrenFromErsatz
            ? sparte.vorversicherungsListe
            : [],
        };
      });
    }
    case CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_ADD_ITEM: {
      const { appId, angebotsId }: AngebotsId = action.payload;
      return replaceMatching(sectionState, appId, angebotsId, sparte => {
        if (!sparte.vorversicherungCanBeAdded) {
          return sparte;
        }
        const nextListe = sparte.vorversicherungsListe.concat({
          versicherungsgesellschaft: null,
          vsnr: null,
          versicherteGefahren: sparte.gefahrenFromErsatz
            ? sparte.vorversicherungsListe[0].versicherteGefahren
            : [...getVersicherteGefahren(appId)],
          ablaufdatum: null,
        });
        return {
          ...sparte,
          vorversicherungsListe: nextListe,
          vorversicherungCanBeAdded: nextListe.length < MAX_VERSICHERUNGSLISTE_LENGTH,
        };
      });
    }
    case CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_DELETE_ITEM: {
      const { appId, angebotsId, itemIndex }: VorversicherungDeleteItem = action.payload;
      return replaceMatching(sectionState, appId, angebotsId, sparte => {
        const nextListe = sparte.vorversicherungsListe.filter(
          (_, index) => index !== itemIndex,
        );
        const nextVorhanden = nextListe.length > 0 ? true : null;
        return {
          ...sparte,
          vorversicherungsListe: nextListe,
          vorhanden: nextVorhanden,
          vorversicherungCanBeAdded:
            nextVorhanden && nextListe.length < MAX_VERSICHERUNGSLISTE_LENGTH,
        };
      });
    }
    case CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_CHANGE_ITEM: {
      const actionPayload: VorversicherungsListeGeaendert = action.payload;
      return replaceMatching(sectionState, actionPayload.appId, actionPayload.angebotsId, sparte => ({
        ...sparte,
        vorversicherungsListe: replaceListItem(
          sparte.vorversicherungsListe,
          actionPayload.changedIndex,
          item => ({ ...item, ...actionPayload.changedVersicherungsItemValues }),
        ),
      }));
    }
    case GlobalActions.VORVERSICHERUNG_ABLAUFDATUM_CHANGED: {
      const actionPayload: VorversicherungAblaufdatumChanged = action.payload;
      return replaceMatching(sectionState, actionPayload.appId, actionPayload.angebotsId, sparte => ({
        ...sparte,
        vorversicherungsListe: replaceListItem(
          sparte.vorversicherungsListe,
          actionPayload.changedIndex,
          item => ({ ...item, ablaufdatum: actionPayload.ablaufdatum }),
        ),
      }));
    }
    case CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_VERSICHERTEGEFAHREN_GEAENDERT: {
      const actionPayload: VorversicherungsListeVersicherteGefahrenAendern = action.payload;
      return replaceMatching(sectionState, actionPayload.appId, actionPayload.angebotsId, sparte => ({
        ...sparte,
        vorversicherungsListe: replaceListItem(
          sparte.vorversicherungsListe,
          actionPayload.changedIndexVorversicherung,
          item => {
            if (actionPayload.isRadioSelection) {
              const changedModelValue = actionPayload.changedModelValue as number;
              return {
                ...item,
                versicherterGefahrIndex: changedModelValue,
                versicherteGefahren: item.versicherteGefahren.map((vg, index) => ({
                  ...vg,
                  selected: index === changedModelValue,
                })),
              };
            }
            const changedIndexGefahr = actionPayload.changedIndexGefahr;
            return {
              ...item,
              versicherteGefahren: item.versicherteGefahren.map((vg, index) =>
                index === changedIndexGefahr
                  ? { ...vg, selected: actionPayload.changedModelValue as boolean }
                  : vg,
              ),
            };
          },
        ),
      }));
    }
    case GlobalActions.PRODUKT_ACTION: {
      const produkt: CheckoutProdukt = action.payload;
      const matchingProdukt = sectionState.some(
        stateVorversicherung =>
          produkt.appId === stateVorversicherung.appId &&
          stateVorversicherung.angebotsId === produkt.angebotsId,
      );
      const unfallForSameVpExists = state.produkte.some(stateProdukt => {
        const isUnfall = produkt.appId === App.UNF;
        const vpAlreadyExists = stateProdukt.versichertesRisiko === produkt.versichertesRisiko;
        return isUnfall && vpAlreadyExists;
      });

      const gefahrenFromErsatz = produkt.gefahrenVorversicherung?.length > 0;
      if (
        !matchingProdukt &&
        !unfallForSameVpExists &&
        (!produkt.vorvertragsnummer?.length || gefahrenFromErsatz)
      ) {
        const vorversicherungToAdd: Vorversicherung = {
          appId: produkt.appId,
          angebotsId: produkt.angebotsId,
          vorhanden: produkt.hasVorversicherung ?? null,
          gefahrenFromErsatz,
          vorversicherungCanBeAdded: produkt.hasVorversicherung,
          vorversicherungsListe:
            produkt.hasVorversicherung || gefahrenFromErsatz
              ? [
                  {
                    versicherungsgesellschaft: null,
                    vsnr: null,
                    versicherteGefahren: gefahrenFromErsatz
                      ? [...produkt.gefahrenVorversicherung]
                      : [...getVersicherteGefahren(produkt.appId)],
                    ablaufdatum: null,
                  },
                ]
              : [],
          warningIfNotVorhanden: setWarningIfNotVorhandenForSparte(produkt.appId),
        };
        return [...sectionState, vorversicherungToAdd];
      } else if (matchingProdukt && gefahrenFromErsatz) {
        return replaceMatching(sectionState, produkt.appId, produkt.angebotsId, sparte => ({
          ...sparte,
          vorversicherungsListe: sparte.vorversicherungsListe.map(item => ({
            ...item,
            versicherteGefahren: produkt.gefahrenVorversicherung,
          })),
        }));
      } else if (matchingProdukt && !gefahrenFromErsatz && produkt.vorvertragsnummer?.length > 0) {
        return sectionState.filter(
          vorversicherung => !(vorversicherung.appId === produkt.appId && vorversicherung.angebotsId === produkt.angebotsId),
        );
      } else if (matchingProdukt && produkt.appId === App.GEGE) {
        const hasFahrrad = (produkt as unknown as GegeCheckoutProdukt).gegenstaende?.some(
          g => g.kategorie.id === GegenstandCategory.FAHRRAD,
        );
        if (!hasFahrrad) {
          return replaceMatching(sectionState, produkt.appId, produkt.angebotsId, sparte => ({
            ...sparte,
            vorhanden: null,
            vorversicherungsListe: [],
          }));
        }
      }
      return sectionState;
    }
    case GlobalActions.PRODUKT_DELETED: {
      const deletedProduktPayload: ProduktDeleted = action.payload;
      return sectionState.filter(
        vorversicherung =>
          !deletedProduktPayload.angebotsIds.some(
            angebotsId =>
              angebotsId.appId === vorversicherung.appId &&
              vorversicherung.angebotsId === angebotsId.angebotsId,
          ),
      );
    }
    case GlobalActions.SOFORTSCHUTZ_AVAILABLE: {
      const actionPayload: SofortschutzAvailableItem[] = action.payload;
      let changed = false;
      const next = sectionState.map(vorversicherung => {
        if (vorversicherung.gefahrenFromErsatz) {
          return vorversicherung;
        }
        const sofortschutzAvailableItem = actionPayload.find(
          item =>
            item.spartenType === getSpartenTypeByApp(vorversicherung.appId) &&
            (!vorversicherung.angebotsId || vorversicherung.angebotsId === item.angebotsId),
        );
        if (sofortschutzAvailableItem?.isAvailable) {
          return vorversicherung;
        }
        const hasAblaufdatum = vorversicherung.vorversicherungsListe.some(
          item => item.ablaufdatum != null,
        );
        if (!hasAblaufdatum) {
          return vorversicherung;
        }
        changed = true;
        return {
          ...vorversicherung,
          vorversicherungsListe: vorversicherung.vorversicherungsListe.map(item =>
            item.ablaufdatum == null ? item : { ...item, ablaufdatum: null },
          ),
        };
      });
      return changed ? next : sectionState;
    }
    default:
      return sectionState;
  }
};

function setWarningIfNotVorhandenForSparte(appId: App): boolean {
  return appId === App.VWG;
}
